import React, { useState, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { getCategoriesGroupedByType } from '../../lib/supabase';
import type { CampCategory } from '../../types/database';

interface AdvancedFiltersProps {
  selectedCategories: string[];
  onCategoryChange: (categoryIds: string[]) => void;
  className?: string;
}

export default function AdvancedFilters({ 
  selectedCategories, 
  onCategoryChange, 
  className = '' 
}: AdvancedFiltersProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [categories, setCategories] = useState<Record<string, CampCategory[]>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadCategories() {
      const groupedCategories = await getCategoriesGroupedByType();
      if (groupedCategories) {
        setCategories(groupedCategories);
      }
      setLoading(false);
    }

    loadCategories();
  }, []);

  const handleCategoryToggle = (categoryId: string) => {
    const newCategories = selectedCategories.includes(categoryId)
      ? selectedCategories.filter(id => id !== categoryId)
      : [...selectedCategories, categoryId];
    
    onCategoryChange(newCategories);
  };

  const handleClearAll = () => {
    onCategoryChange([]);
  };

  const getSelectedCount = () => {
    // Only count categories that are NOT focus areas (since focus areas are shown as chips outside)
    const nonFocusAreaCategories = selectedCategories.filter(categoryId => {
      // Check if this category is NOT in the focus_area group
      return !categories.focus_area?.some(cat => cat.id === categoryId);
    });
    return nonFocusAreaCategories.length;
  };

  const hasSelectedFilters = getSelectedCount() > 0;

  return (
    <div className="relative">
      {/* More Filters Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`category-chip flex items-center gap-2 ${
          hasSelectedFilters ? 'category-chip-selected' : 'category-chip-default'
        }`}
      >
        <span>More Filters</span>
        {getSelectedCount() > 0 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold min-w-[16px] h-[16px] flex items-center justify-center bg-white text-primary">
            {getSelectedCount()}
          </span>
        )}
        <ChevronDownIcon 
          className={`h-4 w-4 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown Panel - official Tailwind pattern with all sections */}
      {isOpen && (
        <div 
          className="absolute right-0 mt-2 w-80 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-[60] max-h-96 overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="py-1">
            {loading ? (
              <div className="px-4 py-8 text-center text-gray-500">Loading filters...</div>
            ) : (
              <>
                {/* Program Format Section */}
                {categories.format && categories.format.length > 0 && (
                  <>
                    <div className="px-4 py-4">
                      <h4 className="text-sm font-semibold text-gray-900 text-left">Program Format</h4>
                    </div>
                    <div className="space-y-1">
                      {categories.format.map((category) => (
                        <button
                          key={category.id}
                          onClick={() => handleCategoryToggle(category.id)}
                          className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <input
                            type="checkbox"
                            checked={selectedCategories.includes(category.id)}
                            onChange={() => {}}
                            className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          />
                          <span className="ml-3">{category.name}</span>
                        </button>
                      ))}
                    </div>
                    <div className="border-t border-gray-300"></div>
                  </>
                )}

                {/* Age Groups Section */}
                {categories.age_group && categories.age_group.length > 0 && (
                  <>
                    <div className="px-4 py-4">
                      <h4 className="text-sm font-semibold text-gray-900 text-left">Age Groups</h4>
                    </div>
                    {categories.age_group.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => handleCategoryToggle(category.id)}
                        className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(category.id)}
                          onChange={() => {}}
                          className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <span className="ml-3">{category.name}</span>
                      </button>
                    ))}
                    <div className="border-t border-gray-300"></div>
                  </>
                )}

                {/* Support Needs Section */}
                {categories.support_needs && categories.support_needs.length > 0 && (
                  <>
                    <div className="px-4 py-4">
                      <h4 className="text-sm font-semibold text-gray-900 text-left">Support Needs</h4>
                    </div>
                    {categories.support_needs.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => handleCategoryToggle(category.id)}
                        className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(category.id)}
                          onChange={() => {}}
                          className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <span className="ml-3">{category.name}</span>
                      </button>
                    ))}
                    <div className="border-t border-gray-300"></div>
                  </>
                )}

                {/* Values & Inclusion Section */}
                {categories.values && categories.values.length > 0 && (
                  <>
                    <div className="px-4 py-4">
                      <h4 className="text-sm font-semibold text-gray-900 text-left">Values & Inclusion</h4>
                    </div>
                    {categories.values.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => handleCategoryToggle(category.id)}
                        className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(category.id)}
                          onChange={() => {}}
                          className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <span className="ml-3">{category.name}</span>
                      </button>
                    ))}
                    <div className="border-t border-gray-300"></div>
                  </>
                )}

                {/* Organization Type Section */}
                {categories.organization_type && categories.organization_type.length > 0 && (
                  <>
                    <div className="px-4 py-4">
                      <h4 className="text-sm font-semibold text-gray-900 text-left">Organization Type</h4>
                    </div>
                    {categories.organization_type.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => handleCategoryToggle(category.id)}
                        className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(category.id)}
                          onChange={() => {}}
                          className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <span className="ml-3">{category.name}</span>
                      </button>
                    ))}
                    <div className="border-t border-gray-300"></div>
                  </>
                )}

                {/* Footer Actions */}
                <div className="px-4 py-2">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={handleClearAll}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      Clear all
                    </button>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="bg-purple-600 text-white px-3 py-1.5 text-sm font-medium rounded-md hover:bg-purple-700"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}